import React from 'react';
import { AdvancedSettings } from './AdvancedSettings';
import { ChartConfig } from '../../types';

interface ChartTypeSettingsProps {
    config: ChartConfig;
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
    onAdvancedChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
    onUpdateConfig: (updates: Partial<ChartConfig>) => void;
    allCols: string[];
    numericCols: string[];
}

export const ChartTypeSettings: React.FC<ChartTypeSettingsProps> = ({
    config,
    onChange,
    onAdvancedChange,
    onUpdateConfig,
    allCols,
    numericCols
}) => {
    const { type } = config;

    // Advanced Charts (Scientific, Vector, 3D, Map, Globe, 3D Bar, Force Graph)
    if (['scientific', 'vector', '3d', '3d-bar', 'map', 'globe', 'force-directed'].includes(type || '')) {
        return (
            <div className="p-3 border-b border-white/10">
                <AdvancedSettings
                    config={config}
                    onChange={(newAdvanced) => onUpdateConfig({ advanced: newAdvanced })}
                    onConfigChange={onChange}
                    allCols={allCols}
                    numericCols={numericCols}
                />
            </div>
        );
    }

    // Standard charts (bar, line, area, scatter, pie) - minimal controls
    if (['bar', 'line', 'area', 'scatter', 'pie'].includes(type || '')) {
        return (
            <div className="p-3 space-y-3">
                <h4 className="text-[10px] font-bold text-gold-500/70 uppercase tracking-widest">
                    Chart Options
                </h4>

                {/* Only show stacking for bar/area */}
                {['bar', 'area'].includes(type || '') && (
                    <div className="flex items-center justify-between">
                        <label className="text-xs font-medium text-gray-400">Stack Series</label>
                        <input
                            type="checkbox"
                            checked={config.customization?.stacked || false}
                            onChange={(e) => onUpdateConfig({
                                customization: { ...config.customization, stacked: e.target.checked }
                            })}
                            className="h-3.5 w-3.5 rounded border-white/20 bg-white/5 text-gold-500 focus:ring-gold-500"
                        />
                    </div>
                )}
            </div>
        );
    }

    // No specific settings for other chart types
    return null;
};
